import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createRandomID } from '../../../reusable/reusableFunctions.js';
import { SUGGESTIONS } from '../../pbbStrategistHelpers.js';
import { messageManager } from '../../../System/messageManager/messageManager.js';
import { useNameConventions } from '../../../../contexts/nameConventionsContext.js';
import { useChatActions } from '../../ChatActionsContext.js';
import { ForgeAiChatbot } from '@tylertech/forge-ai-react';
import { PbbStrategistAdapter } from '../../ChatbotAdapter/pbbStrategistAdapter.js';
import { formatThreadMessages, updateToolbarInteractivity } from '../../ChatbotAdapter/pbbStrategistAdapterHelpers.js';
import { getThreads, getThreadMessages, deleteThread, renameThread } from '../../../../services/ragService.js';
import { useUser } from '../../../../contexts/userContext.js';
import { ToastComponent } from '@tylertech/forge';
import './Chatbot.scss';

const CustomChatbot = () => {
  const { chatbotRef, onMessageFeedback, setWaitingResponse, waitingResponse, onConversationReset } = useChatActions();
  const { nameConventions } = useNameConventions();
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const { user } = useUser();

  //we need all of this [FOR NOW] because the chatbot call all functions multiple times.
  const deletingThreadsRef = useRef(new Set());
  const renamingThreadsRef = useRef(new Set());
  const submittingFeedbackRef = useRef(new Set());
  const loadingThreadIdRef = useRef(null);
  const selectedThreadIdRef = useRef(selectedThreadId);
  selectedThreadIdRef.current = selectedThreadId;

  // Workaround for the popover not being interactive when it is open and is not the last message. 
  // This is a temporary solution until the Forge AI Chatbot component is updated to handle this properly.
  const enforceFeedbackPopoverInteractivity = useCallback((isOpen = true, sourceEvent = null) => {
    const chatbotEl = chatbotRef.current;
    const root = chatbotEl?.shadowRoot;
    if (!root) return;

    const toolbarFromEventPath = typeof sourceEvent?.composedPath === 'function'
      ? sourceEvent.composedPath().find((el) => el?.tagName === 'FORGE-AI-RESPONSE-MESSAGE-TOOLBAR')
      : null;
    if (toolbarFromEventPath) {
      updateToolbarInteractivity(toolbarFromEventPath, isOpen);
      return;
    }

    const queue = [root];
    while (queue.length) {
      const currentRoot = queue.shift();
      if (!currentRoot) continue;

      currentRoot.querySelectorAll?.('forge-ai-response-message-toolbar').forEach((toolbar) => updateToolbarInteractivity(toolbar, isOpen));

      currentRoot.querySelectorAll?.('*').forEach((el) => {
        if (el.shadowRoot) queue.push(el.shadowRoot);
      });
    }
  }, [chatbotRef]);
  // ends here

  const fetchThreads = useCallback(async () => {
    if (!chatbotRef.current) return;
    try {
      const resp = await getThreads();
      if (chatbotRef.current && resp?.data) {
        chatbotRef.current.recentThreads = resp.data;
      }
    } catch (error) {
      //
    }
  }, [chatbotRef]);

  //we set the adapter and threads like this because the chatbot component does not accept them normally. [FOR NOW]
  const setRef = useCallback((node) => {
    if (!node) return;

    if (!node.adapter) {
      const adapter = new PbbStrategistAdapter();
      const originalUpdateState = adapter._updateState.bind(adapter);

      adapter._updateState = (state) => {
        originalUpdateState(state);
        if (state && typeof state.isRunning === 'boolean') {
          setWaitingResponse(state.isRunning);
          if (!state.isRunning) {
            fetchThreads();
            if (chatbotRef.current && chatbotRef.current.getMessages()?.pop()?.role !== 'system') handleGetThreadMessages(chatbotRef.current.adapter.threadId);
          }
        }
      };
      adapter.setChatbotElement(node);
      node.adapter = adapter;
    }

    // Workaround for the popover not being interactive when it is open and is not the last message.
    // This is a temporary solution until the Forge AI Chatbot component is updated to handle this properly.
    if (!node.__pbbPopoverToggleBound) {
      node.addEventListener('forge-ai-popover-toggle', (event) => {
        const isOpen = Boolean(event?.detail?.open);
        requestAnimationFrame(() => enforceFeedbackPopoverInteractivity(isOpen, event));
      });
      node.__pbbPopoverToggleBound = true;
    }

    chatbotRef.current = node;
    requestAnimationFrame(() => enforceFeedbackPopoverInteractivity(true));
  }, [chatbotRef, setWaitingResponse, fetchThreads, enforceFeedbackPopoverInteractivity]);
  // ends here

  const handleGetThreadMessages = async (threadId) => {
    try {
      if (chatbotRef.current.adapter) chatbotRef.current.adapter.threadId = threadId;

      const response = await getThreadMessages(threadId);
      const rawMessages = response?.data || [];
      const formattedMessages = formatThreadMessages(rawMessages);
      chatbotRef.current.setMessages(formattedMessages);
      setSelectedThreadId(threadId);
    } catch (error) {
      ToastComponent.present({
        message: messageManager(nameConventions).PBB_STRATEGIST.CONVERSATION.GET_MESSAGES_ERROR,
      });
    } finally {
      loadingThreadIdRef.current = null;
    }
  };

  const handleThreadSelect = async (e) => {
    if (e?.stopImmediatePropagation) e.stopImmediatePropagation();
    if (e?.stopPropagation) e.stopPropagation();
    if (e?.cancelable && typeof e?.preventDefault === 'function') e.preventDefault();

    if (waitingResponse) return;

    const threadId = e?.detail?.threadId || e?.detail?.id;

    if (!threadId || !chatbotRef.current) return;
    if (selectedThreadIdRef.current === threadId || loadingThreadIdRef.current === threadId) return;

    loadingThreadIdRef.current = threadId;
    await handleGetThreadMessages(threadId);
  };

  const handleNewChat = (e) => {
    if (e?.cancelable && typeof e?.preventDefault === 'function') e.preventDefault();
    if (waitingResponse || !chatbotRef.current) return;

    if (typeof chatbotRef.current.abort === 'function') chatbotRef.current.abort();
    else if (chatbotRef.current.adapter?.abort) chatbotRef.current.adapter.abort();

    if (chatbotRef.current.adapter) chatbotRef.current.adapter.threadId = createRandomID();

    setSelectedThreadId(null);
    onConversationReset();
    fetchThreads();
  };

  const handleThreadDelete = async (e) => {
    if (e?.stopImmediatePropagation) e.stopImmediatePropagation();
    if (e?.stopPropagation) e.stopPropagation();
    if (e?.cancelable && typeof e?.preventDefault === 'function') e.preventDefault();

    const threadId = e?.detail?.id || e?.detail?.threadId;
    if (!threadId) return;

    if (deletingThreadsRef.current.has(threadId)) return;
    deletingThreadsRef.current.add(threadId);

    try {
      await deleteThread(threadId);

      if (typeof e?.detail?.onSuccess === 'function') {
        e.detail.onSuccess();
      }
      ToastComponent.present({
        message: messageManager(nameConventions).PBB_STRATEGIST.CONVERSATION.DELETE_SUCCESS,
      });

      if (selectedThreadIdRef.current === threadId) {
        handleNewChat();
      } else {
        fetchThreads();
      }
    } catch (error) {
      ToastComponent.present({
        message: messageManager(nameConventions).PBB_STRATEGIST.CONVERSATION.DELETE_ERROR,
      });
    } finally {
      deletingThreadsRef.current.delete(threadId);
    }
  };

  const handleThreadRename = async (e) => {
    if (e?.stopImmediatePropagation) e.stopImmediatePropagation();
    if (e?.stopPropagation) e.stopPropagation();
    if (e?.cancelable && typeof e?.preventDefault === 'function') e.preventDefault();

    const threadId = e?.detail?.id || e?.detail?.threadId;
    const newTitle = e?.detail?.newTitle;

    if (!threadId || !newTitle?.trim()) return;

    if (renamingThreadsRef.current.has(threadId)) return;
    renamingThreadsRef.current.add(threadId);

    try {
      await renameThread({ threadId, title: newTitle.trim() });

      if (typeof e?.detail?.onSuccess === 'function') {
        e.detail.onSuccess();
      }
      ToastComponent.present({
        message: messageManager(nameConventions).PBB_STRATEGIST.CONVERSATION.RENAME_SUCCESS,
      });
      fetchThreads();
    } catch (error) {
      if (typeof e?.detail?.onError === 'function') {
        e.detail.onError(error);
      }
      ToastComponent.present({
        message: messageManager(nameConventions).PBB_STRATEGIST.CONVERSATION.RENAME_ERROR,
      });
    } finally {
      renamingThreadsRef.current.delete(threadId);
    }
  };

  const handleResponseFeedback = async (e) => {
    if (e?.stopImmediatePropagation) e.stopImmediatePropagation();
    if (e?.stopPropagation) e.stopPropagation();
    if (e?.cancelable && typeof e?.preventDefault === 'function') e.preventDefault();

    const type = e?.detail?.type;
    const feedback = e?.detail?.feedback;
    const rawMessageId = e?.detail?.messageId || e?.detail?.details?.messageId;
    const adapter = chatbotRef.current?.adapter;
    const resolve = (id) => (typeof adapter?.resolveMessageId === 'function' ? adapter.resolveMessageId(id) : id);
    const currentMessages = typeof chatbotRef.current?.getMessages === 'function' ? (chatbotRef.current.getMessages() || []) : [];

    let messageId = resolve(rawMessageId);
    if (messageId === rawMessageId) {
      const rawMessage = currentMessages.find((message) => message?.id === rawMessageId);
      const childResolvedId = rawMessage?.children
        ?.map((child) => resolve(child?.messageId))
        .find((id, index) => id && id !== rawMessage.children[index]?.messageId);
      if (childResolvedId) messageId = childResolvedId;
    }

    if (messageId !== rawMessageId && typeof adapter?.setMessageIdAlias === 'function') {
      adapter.setMessageIdAlias(rawMessageId, messageId);
    }

    if (!messageId) return;

    const lockKey = `${messageId}-${type}`;
    if (submittingFeedbackRef.current.has(lockKey)) return;

    submittingFeedbackRef.current.add(lockKey);

    try {
      if (typeof onMessageFeedback === 'function') {
        await onMessageFeedback(type, feedback, messageId);
      }
    } catch (error) {
      ToastComponent.present({
        message: messageManager(nameConventions).PBB_STRATEGIST.CONVERSATION.HANDLE_FEEDBACK_ERROR,
      });
    } finally {
      submittingFeedbackRef.current.delete(lockKey);
    }
  };

  useEffect(() => {
    fetchThreads();
    setSelectedThreadId(null);
  }, [fetchThreads, user?.activeOrganization]);

  return (
    <div className="chatbot-container">
      <ForgeAiChatbot
        ref={setRef}
        suggestions={SUGGESTIONS}
        selectedThreadId={selectedThreadId}
        enableReactions={!waitingResponse}
        showConversationsButton={!waitingResponse}
        showConversationRename
        showConversationDelete
        exportOption={waitingResponse ? 'off' : 'on'}
        clearOption={waitingResponse ? 'off' : 'on'}
        voiceInput="off"
        titleText={messageManager(nameConventions).PBB_STRATEGIST.TITLE}
        onForgeAiChatbotConversationSelect={handleThreadSelect}
        onForgeAiChatbotConversationDelete={handleThreadDelete}
        onForgeAiChatbotConversationRename={handleThreadRename}
        onForgeAiChatbotNewChat={handleNewChat}
        onForgeAiChatbotResponseFeedback={handleResponseFeedback}
        onForgeAiChatbotClear={handleNewChat}
      />
    </div>
  );
};

export default CustomChatbot;
